/* eslint-disable sonarjs/cognitive-complexity */
import { useState, useContext, useEffect } from 'react';
import { navigate } from 'gatsby';
import { useLocation } from '@reach/router';

import { AuthContext } from 'context/AuthContext';
import { urls, isValidHost, isPathInKnownUrls } from 'utils/urls';
import { apiLogin } from 'utils/api';

const validateUrl = (param) => {
  try {
    return new URL(param);
  } catch {
    return param;
  }
};

export const useSignin = (redirect) => {
  const location = useLocation();
  const { setLogin, getUserData, isLoggedIn } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const nextParam = typeof window === 'undefined' ? '' : new URLSearchParams(window.location.search).get('next');
  const url = nextParam && validateUrl(nextParam);
  const redirectPath =
    (isValidHost(url?.host) && nextParam) ||
    (isPathInKnownUrls(redirect, urls) && redirect) ||
    (isPathInKnownUrls(location?.state?.redirect, urls) && location?.state?.redirect) ||
    '';

  useEffect(() => {
    if (isLoggedIn && !redirectPath.startsWith('/nessy') && !redirectPath.startsWith('/admin')) {
      navigate(redirectPath || '/', { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submitHandler = async (e) => {
    e.preventDefault();

    const fd = new FormData(e.target);
    if (!fd.get('username')) return setError('Please enter your email/username.');
    if (!fd.get('password')) return setError('Please enter your password.');
    if (error) setError('');

    setLoading(true);
    try {
      const user = await apiLogin(fd);
      if (user) {
        setLogin(user);
        if (redirectPath) await getUserData();
        navigate(redirectPath || urls.dashboard);
      }
    } catch (err) {
      setLoading(false);
      if (err?.data?.totp) return navigate(urls.accounts.login_2fa, { state: { redirect: redirectPath } });
      if (err?.data?.sms) return navigate(urls.accounts.login_2fa_sms, { state: { redirect: redirectPath } });
      if (err?.data?.captcha) return navigate(urls.accounts.login_captcha, { state: { redirect: redirectPath } });

      setError(err?.data?.non_field_errors?.[0] || 'Something went wrong. Please try again or contact support!');
      window?.grecaptcha?.reset?.();
      window?.onCaptcha?.();
    }
  };

  const clearError = (field) => {
    if (error) {
      if (field) {
        setError({ ...error, [field]: undefined });
      } else {
        setError('');
      }
    }
  };

  return { submitHandler, loading, error, clearError };
};
